import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/components/auth/AuthProvider";
import { useSubscription } from "@/hooks/use-subscription";
import { SignupNudge } from "./SignupNudge";
import { PremiumNudge } from "./PremiumNudge";

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTED_TOPICS = [
  { label: "I'm looking into care for a family member", icon: "👨‍👩‍👧" },
  { label: "We need to find a nursing home soon", icon: "🏠" },
  { label: "I want to understand the Fair Deal scheme", icon: "💶" },
  { label: "I'm not sure what type of care we need", icon: "🤔" },
  { label: "We're considering home care options", icon: "🏡" },
  { label: "I'm feeling overwhelmed and need guidance", icon: "💛" },
];

const DEFAULT_FOLLOW_UPS = [
  "Tell me more about that",
  "What should we do next?",
  "Can you explain that simply?",
  "What are the costs involved?",
];

function extractSuggestions(content: string): { clean: string; suggestions: string[] } {
  const match = content.match(/<!--suggestions:\[(.+?)\]-->\s*$/);
  if (!match) return { clean: content, suggestions: [] };
  try {
    const suggestions = JSON.parse(`[${match[1]}]`) as string[];
    return { clean: content.replace(match[0], "").trimEnd(), suggestions };
  } catch {
    return { clean: content, suggestions: [] };
  }
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/care-chat`;

async function streamChat({
  messages,
  onDelta,
  onDone,
  onError,
  authToken,
}: {
  messages: Msg[];
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (err: string) => void;
  authToken?: string | null;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ messages }),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    onError(data.error || "Something went wrong. Please try again.");
    return;
  }

  if (!resp.body) {
    onError("No response received.");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  // Final flush
  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch { /* ignore */ }
    }
  }

  onDone();
}

const PREMIUM_TRIGGER_TOPICS = [
  "cost", "price", "afford", "expensive", "cheap",
  "compare", "comparison", "versus", "vs",
  "fair deal calculator", "financial assessment", "means test",
  "shortlist", "enquiry", "enquiries", "track",
];

function hasPremiumTrigger(messages: Msg[]): boolean {
  const lastUserMsgs = messages.filter(m => m.role === "user").slice(-3);
  return lastUserMsgs.some(m =>
    PREMIUM_TRIGGER_TOPICS.some(topic => m.content.toLowerCase().includes(topic))
  );
}

const CHAT_STORAGE_KEY = "grandpal_care_chat_messages";

export function CareChat() {
  const { user, session } = useAuth();
  const { subscribed } = useSubscription();
  const [messages, setMessages] = useState<Msg[]>(() => {
    try {
      const saved = sessionStorage.getItem(CHAT_STORAGE_KEY);
      if (saved) {
        sessionStorage.removeItem(CHAT_STORAGE_KEY);
        return JSON.parse(saved) as Msg[];
      }
    } catch { /* ignore */ }
    return [];
  });
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [signupDismissed, setSignupDismissed] = useState(false);
  const [premiumDismissed, setPremiumDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const saveMessagesToStorage = () => {
    try {
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    } catch { /* ignore */ }
  };

  const userMsgCount = messages.filter(m => m.role === "user").length;

  // Signup nudge: after 3 messages, not logged in, not dismissed
  const showSignupNudge = !user && userMsgCount >= 3 && !signupDismissed;

  // Premium nudge: logged in, not subscribed, not dismissed, after 5 messages OR topic trigger
  const showPremiumNudge =
    !!user &&
    !subscribed &&
    !premiumDismissed &&
    (userMsgCount >= 5 || hasPremiumTrigger(messages));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: Msg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    try {
      await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsertAssistant,
        onDone: () => setIsLoading(false),
        onError: (err) => {
          toast.error(err);
          setIsLoading(false);
        },
        authToken: session?.access_token,
      });
    } catch {
      toast.error("Connection error. Please try again.");
      setIsLoading(false);
    }
  };

  const hasStarted = messages.length > 0;

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] max-w-3xl mx-auto">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {!hasStarted && (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-8">
            <div className="bg-primary/10 rounded-full p-4">
              <MessageCircle className="h-10 w-10 text-primary" />
            </div>
            <div>
             <h2 className="text-2xl font-bold font-lora text-foreground mb-2">
                Hello! I'm your Care Advisor
              </h2>
              <p className="text-muted-foreground max-w-md">
                I'm here to help you navigate care options in Ireland — at your pace. 
                Let's start by understanding your situation.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
              {SUGGESTED_TOPICS.map((topic) => (
                <button
                  key={topic.label}
                  onClick={() => sendMessage(topic.label)}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:bg-accent/50 text-left text-sm transition-colors"
                >
                  <span className="text-lg">{topic.icon}</span>
                  <span className="text-foreground">{topic.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground"
              }`}
            >
              {msg.role === "assistant" ? (
                <div className="prose prose-sm max-w-none dark:prose-invert [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1">
                  <ReactMarkdown>{extractSuggestions(msg.content).clean}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl px-4 py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}

        {/* Follow-up suggestions after assistant response */}
        {hasStarted && !isLoading && messages[messages.length - 1]?.role === "assistant" && (() => {
          const lastMsg = messages[messages.length - 1];
          const { suggestions } = extractSuggestions(lastMsg.content);
          const chips = suggestions.length > 0 ? suggestions : DEFAULT_FOLLOW_UPS;
          return (
            <div className="flex flex-wrap gap-2 pl-2">
              {chips.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => sendMessage(suggestion)}
                  className="text-xs px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Signup nudge for unauthenticated users */}
        {showSignupNudge && (
          <SignupNudge
            onDismiss={() => setSignupDismissed(true)}
            onBeforeNavigate={saveMessagesToStorage}
          />
        )}

        {/* Premium upsell for free users */}
        {showPremiumNudge && (
          <PremiumNudge onDismiss={() => setPremiumDismissed(true)} />
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-border bg-background p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage(input);
          }}
          className="flex gap-2 max-w-3xl mx-auto"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything about care in Ireland..."
            disabled={isLoading}
            className="flex-1"
          />
          <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
